// src/components/TimelineView.jsx
// Gantt-style timeline view for deals and tasks

import React, { useState, useMemo, useEffect } from 'react';
import { 
  ChevronLeft, ChevronRight, Calendar, Filter, Building2, 
  CheckCircle2, Clock, AlertCircle, FileText, Loader2, ArrowLeft, LogOut
} from 'lucide-react';
import { supabase } from '../lib/supabase';

// LFG Brand Colors
const LFG_COLORS = {
  navy: '#1A3665',
  gray: '#707578',
  green: '#85BD66',
};

// Fallback when stages aren't loaded.
const DEFAULT_PHASES = [
  { id: 'future-target', name: 'Future Target', color: '#94A3B8' },
  { id: 'prospect',      name: 'Prospect',      color: '#6B7280' },
  { id: 'diligence',     name: 'Diligence',     color: '#EAB308' },
  { id: 'offer-made',    name: 'Offer Made',    color: '#F97316' },
  { id: 'under-loi',     name: 'Under LOI',     color: '#3B82F6' },
  { id: 'on-hold',       name: 'On Hold',       color: '#A855F7' },
  { id: 'closed',        name: 'Closed',        color: '#22C55E' },
  { id: 'canceled',      name: 'Canceled',      color: '#EF4444' },
];

export default function TimelineView({ 
  deals, 
  onSelectDeal, 
  onBack,
  pc = LFG_COLORS.navy, 
  sc = LFG_COLORS.gray,
  stages = [],
  signOut
}) {
  // Use provided stages or fallback to defaults
  const PHASES = stages?.length > 0 ? stages : DEFAULT_PHASES;
  
  const [viewMode, setViewMode] = useState('month'); // 'week', 'month', 'quarter'
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    today.setDate(1); // Start of current month
    return today;
  });
  const [expandedDeals, setExpandedDeals] = useState({});
  const [dealTasks, setDealTasks] = useState({}); // { dealId: [tasks] }
  const [loadingTasks, setLoadingTasks] = useState({});
  const [filter, setFilter] = useState('all'); // 'all', 'active', 'integration'

  // Fetch tasks when a deal is expanded
  const fetchTasksForDeal = async (dealId) => {
    if (dealTasks[dealId]) return; // Already loaded
    
    setLoadingTasks(prev => ({ ...prev, [dealId]: true }));
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select('id, name, status, due_date, phase, is_doc_request')
        .eq('deal_id', dealId)
        .not('due_date', 'is', null)
        .order('due_date', { ascending: true });
      
      if (error) throw error;
      setDealTasks(prev => ({ ...prev, [dealId]: data || [] }));
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setDealTasks(prev => ({ ...prev, [dealId]: [] }));
    } finally {
      setLoadingTasks(prev => ({ ...prev, [dealId]: false }));
    }
  };

  // Toggle deal expansion
  const toggleDeal = (dealId) => {
    const isExpanding = !expandedDeals[dealId];
    setExpandedDeals(prev => ({ ...prev, [dealId]: isExpanding }));
    
    if (isExpanding) {
      fetchTasksForDeal(dealId);
    }
  };

  // Calculate date range based on view mode
  const dateRange = useMemo(() => {
    const start = new Date(startDate);
    const end = new Date(startDate);
    
    if (viewMode === 'week') {
      start.setDate(start.getDate() - start.getDay()); // Start of week
      end.setDate(start.getDate() + 7 * 4); // 4 weeks
    } else if (viewMode === 'month') {
      start.setDate(1);
      end.setMonth(end.getMonth() + 3); // 3 months
    } else {
      start.setDate(1);
      end.setMonth(end.getMonth() + 6); // 6 months for quarter view
    }
    
    return { start, end };
  }, [startDate, viewMode]);

  // Generate column headers (dates)
  const columns = useMemo(() => {
    const cols = [];
    const current = new Date(dateRange.start);
    
    while (current <= dateRange.end) {
      if (viewMode === 'week') {
        cols.push({
          date: new Date(current),
          label: current.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          isToday: current.toDateString() === new Date().toDateString(),
        });
        current.setDate(current.getDate() + 1);
      } else if (viewMode === 'month') {
        cols.push({
          date: new Date(current),
          label: current.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          isToday: current.toDateString() === new Date().toDateString(),
          isWeekStart: current.getDay() === 0,
        });
        current.setDate(current.getDate() + 1);
      } else {
        // Quarter view - show weeks
        cols.push({
          date: new Date(current),
          label: `W${Math.ceil(current.getDate() / 7)} ${current.toLocaleDateString('en-US', { month: 'short' })}`,
          isToday: false,
        });
        current.setDate(current.getDate() + 7);
      }
    }
    
    return cols;
  }, [dateRange, viewMode]);

  // Filter deals. Alfie: "should only be LOI signed or better, anything
  // before doesn't need to be on there" — a baseline exclusion applied
  // ahead of the all/active/integration filter, not an optional toggle.
  const loiSignedDeals = useMemo(() => deals.filter(d => !!d.loi_date), [deals]);

  const filteredDeals = useMemo(() => {
    if (filter === 'active') {
      return loiSignedDeals.filter(d => !d.stage?.startsWith('integration') && d.stage !== 'close');
    } else if (filter === 'integration') {
      return loiSignedDeals.filter(d => d.stage?.startsWith('integration') || d.stage === 'close');
    }
    return loiSignedDeals;
  }, [loiSignedDeals, filter]);

  // Navigate time
  const navigate = (direction) => {
    const newDate = new Date(startDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + direction);
    } else {
      newDate.setMonth(newDate.getMonth() + (direction * 3));
    }
    setStartDate(newDate);
  };

  const goToToday = () => {
    const today = new Date();
    if (viewMode === 'week') {
      today.setDate(today.getDate() - today.getDay());
    } else {
      today.setDate(1);
    }
    setStartDate(today);
  };

  // Calculate position and width for a bar given start/end dates
  const getBarStyle = (startDateStr, endDateStr, color) => {
    if (!startDateStr) return null;
    
    const start = new Date(startDateStr);
    const end = endDateStr ? new Date(endDateStr) : new Date(start.getTime() + 86400000); // 1 day if no end
    
    const rangeStart = dateRange.start.getTime();
    const rangeEnd = dateRange.end.getTime();
    const totalRange = rangeEnd - rangeStart;
    
    // Check if bar is visible in current range
    if (end.getTime() < rangeStart || start.getTime() > rangeEnd) {
      return null;
    }
    
    const barStart = Math.max(start.getTime(), rangeStart);
    const barEnd = Math.min(end.getTime(), rangeEnd);
    
    const leftPercent = ((barStart - rangeStart) / totalRange) * 100;
    const widthPercent = ((barEnd - barStart) / totalRange) * 100;
    
    return {
      left: `${leftPercent}%`,
      width: `${Math.max(widthPercent, 0.5)}%`,
      backgroundColor: color,
    };
  };

  // Get a marker position for a single date (for tasks)
  const getMarkerStyle = (dateStr, color) => {
    if (!dateStr) return null;
    
    const date = new Date(dateStr);
    const rangeStart = dateRange.start.getTime();
    const rangeEnd = dateRange.end.getTime();
    
    if (date.getTime() < rangeStart || date.getTime() > rangeEnd) {
      return null;
    }
    
    const leftPercent = ((date.getTime() - rangeStart) / (rangeEnd - rangeStart)) * 100;
    
    return {
      left: `${leftPercent}%`,
      backgroundColor: color,
    };
  };

  // Get today line position
  const todayPosition = useMemo(() => {
    const today = new Date();
    const rangeStart = dateRange.start.getTime();
    const rangeEnd = dateRange.end.getTime();
    
    if (today.getTime() < rangeStart || today.getTime() > rangeEnd) {
      return null;
    }
    
    return ((today.getTime() - rangeStart) / (rangeEnd - rangeStart)) * 100;
  }, [dateRange]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-8 pb-12 flex flex-col" style={{ minHeight: 'calc(100vh - 4rem)' }}>
      {/* Page heading + controls */}
      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1
            className="text-2xl font-semibold tracking-tight"
            style={{
              background: 'linear-gradient(135deg, #0F172A 0%, #1A3665 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.02em',
            }}
          >
            Timeline
          </h1>
          <p className="text-sm mt-1" style={{ color: '#475569' }}>
            Gantt-style view of every deal's tasks and milestones
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={filter}
            onChange={e => setFilter(e.target.value)}
            className="h-9 text-sm rounded-lg px-3 cursor-pointer focus:outline-none"
            style={{ border: '1px solid #E5E9F0', color: '#0F172A', backgroundColor: 'white' }}
          >
            <option value="all">All Deals</option>
            <option value="active">Active Deals</option>
            <option value="integration">Integration</option>
          </select>

          <div className="h-9 inline-flex rounded-lg overflow-hidden" style={{ border: '1px solid #E5E9F0' }}>
            {['week', 'month', 'quarter'].map(mode => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className="px-3 text-[13px] font-medium capitalize transition-all"
                style={viewMode === mode ? {
                  background: `linear-gradient(135deg, ${pc} 0%, #2E4B7C 100%)`,
                  color: 'white',
                } : {
                  backgroundColor: 'white',
                  color: '#475569',
                }}
              >
                {mode}
              </button>
            ))}
          </div>

          <div className="h-9 inline-flex items-center rounded-lg overflow-hidden" style={{ border: '1px solid #E5E9F0', backgroundColor: 'white' }}>
            <button onClick={() => navigate(-1)} className="h-full px-2 hover:bg-slate-50 transition-colors">
              <ChevronLeft className="w-4 h-4" style={{ color: '#475569' }} />
            </button>
            <button
              onClick={goToToday}
              className="h-full px-3 text-[13px] font-medium transition-colors hover:bg-slate-50"
              style={{ borderLeft: '1px solid #E5E9F0', borderRight: '1px solid #E5E9F0', color: '#334155' }}
            >
              Today
            </button>
            <button onClick={() => navigate(1)} className="h-full px-2 hover:bg-slate-50 transition-colors">
              <ChevronRight className="w-4 h-4" style={{ color: '#475569' }} />
            </button>
          </div>
        </div>
      </div>

      {/* Timeline card */}
      <div
        className="flex-1 rounded-2xl overflow-hidden flex flex-col min-h-0"
        style={{
          backgroundColor: '#ffffff',
          border: '1px solid #E5E9F0',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.03)',
        }}
      >

      {/* Timeline Grid */}
      <div className="flex-1 overflow-auto">
        <div className="min-w-[1200px]">
          {/* Date Headers */}
          <div
            className="sticky top-0 z-10 flex"
            style={{
              backgroundColor: '#FAFBFD',
              borderBottom: '1px solid #E5E9F0',
            }}
          >
            <div
              className="w-72 flex-shrink-0 px-4 py-2.5 text-[10px] font-semibold uppercase"
              style={{ letterSpacing: '0.08em', color: '#64748B', borderRight: '1px solid #E5E9F0' }}
            >
              Deal / Task
            </div>
            <div className="flex-1 flex relative">
              {viewMode === 'month' && (
                <div className="absolute top-0 left-0 right-0 flex" style={{ borderBottom: '1px solid #E5E9F0' }}>
                  {(() => {
                    const months = [];
                    let currentMonth = null;
                    let startIdx = 0;

                    columns.forEach((col, idx) => {
                      const month = col.date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                      if (month !== currentMonth) {
                        if (currentMonth) {
                          months.push({ month: currentMonth, start: startIdx, count: idx - startIdx });
                        }
                        currentMonth = month;
                        startIdx = idx;
                      }
                    });
                    if (currentMonth) {
                      months.push({ month: currentMonth, start: startIdx, count: columns.length - startIdx });
                    }

                    return months.map((m, i) => (
                      <div
                        key={i}
                        className="text-center text-[10px] font-semibold uppercase py-1.5"
                        style={{
                          width: `${(m.count / columns.length) * 100}%`,
                          letterSpacing: '0.08em',
                          color: '#1A3665',
                          borderRight: '1px solid #F0F2F6',
                        }}
                      >
                        {m.month}
                      </div>
                    ));
                  })()}
                </div>
              )}
              <div className={`flex w-full ${viewMode === 'month' ? 'mt-7' : ''}`}>
                {columns.map((col, i) => (
                  <div
                    key={i}
                    className="flex-1 text-center text-[11px] py-1.5 tabular-nums"
                    style={{
                      color: col.isToday ? '#1A3665' : '#94A3B8',
                      backgroundColor: col.isToday ? '#EFF4FB' : 'transparent',
                      borderRight: '1px solid #F0F2F6',
                      borderLeft: col.isWeekStart ? '1px solid #E5E9F0' : undefined,
                      minWidth: viewMode === 'week' ? '40px' : '20px',
                      fontWeight: col.isToday ? 700 : 500,
                    }}
                  >
                    {viewMode !== 'month' || col.date.getDay() === 1 || col.date.getDate() === 1
                      ? <span>{col.date.getDate()}</span>
                      : null}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Deals */}
          {filteredDeals.length === 0 ? (
            <div className="p-10 text-center text-[13px]" style={{ color: '#94A3B8' }}>
              No deals to display. Adjust your filter or create a new deal.
            </div>
          ) : (
            filteredDeals.map(deal => {
              const phase = PHASES.find(p => p.id === deal.stage);
              const phaseColor = phase?.color || '#6b7280';
              const isExpanded = expandedDeals[deal.id];
              const dealBar = getBarStyle(deal.loi_date || deal.created_at, deal.target_close_date, phaseColor);
              const tasks = dealTasks[deal.id] || [];
              const isLoadingTasks = loadingTasks[deal.id];

              return (
                <div key={deal.id} style={{ borderBottom: '1px solid #F0F2F6' }}>
                  {/* Deal Row */}
                  <div
                    className="flex group transition-colors"
                    style={{ backgroundColor: isExpanded ? '#FAFBFD' : 'transparent' }}
                  >
                    <div
                      className="w-72 flex-shrink-0 px-4 py-2.5 flex items-center gap-2 cursor-pointer hover:bg-[#FAFBFD] transition-colors"
                      style={{ borderRight: '1px solid #F0F2F6' }}
                      onClick={() => toggleDeal(deal.id)}
                    >
                      <button className="p-0.5 rounded-md transition-colors hover:bg-white">
                        <ChevronRight
                          className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                          style={{ color: '#94A3B8' }}
                        />
                      </button>
                      <span
                        className="w-6 h-6 flex-shrink-0 rounded-md inline-flex items-center justify-center"
                        style={{ backgroundColor: `${phaseColor}18` }}
                      >
                        <Building2 className="w-3.5 h-3.5" style={{ color: phaseColor }} />
                      </span>
                      <span
                        className="text-[13px] font-semibold truncate cursor-pointer hover:underline"
                        style={{ color: '#0F172A' }}
                        onClick={(e) => { e.stopPropagation(); onSelectDeal(deal.id); }}
                      >
                        {deal.name}
                      </span>
                      <span
                        className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-md ml-auto flex-shrink-0"
                        style={{
                          backgroundColor: `${phaseColor}18`,
                          color: phaseColor,
                          letterSpacing: '0.04em',
                        }}
                      >
                        {phase?.name || 'Unknown'}
                      </span>
                    </div>
                    <div className="flex-1 relative py-2.5">
                      {todayPosition !== null && (
                        <div
                          className="absolute top-0 bottom-0 z-10"
                          style={{ left: `${todayPosition}%`, width: '2px', backgroundColor: '#EF4444' }}
                        />
                      )}
                      {dealBar && (
                        <div
                          className="absolute h-6 rounded-md top-1/2 -translate-y-1/2 cursor-pointer transition-all"
                          style={{
                            ...dealBar,
                            background: `linear-gradient(135deg, ${phaseColor} 0%, ${phaseColor}CC 100%)`,
                            boxShadow: `0 1px 3px ${phaseColor}33, inset 0 1px 0 rgba(255,255,255,0.2)`,
                          }}
                          title={`${deal.name}: ${deal.loi_date || 'No LOI'} → ${deal.target_close_date || 'No target'}`}
                          onClick={() => onSelectDeal(deal.id)}
                        />
                      )}
                    </div>
                  </div>

                  {/* Task Rows (when expanded) */}
                  {isExpanded && (
                    <>
                      {isLoadingTasks ? (
                        <div
                          className="flex items-center gap-2 px-4 py-2 pl-12"
                          style={{ backgroundColor: '#FAFBFD' }}
                        >
                          <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: '#94A3B8' }} />
                          <span className="text-[12px]" style={{ color: '#94A3B8' }}>Loading tasks...</span>
                        </div>
                      ) : tasks.length === 0 ? (
                        <div
                          className="px-4 py-2 pl-12 text-[12px]"
                          style={{ backgroundColor: '#FAFBFD', color: '#94A3B8' }}
                        >
                          No tasks with due dates
                        </div>
                      ) : (
                        tasks.map(task => {
                          const markerColor =
                            task.status === 'Complete' ? LFG_COLORS.green :
                            task.status === 'Requested' ? '#EAB308' :
                            task.status === 'N/A' ? '#94A3B8' :
                            '#1A3665';
                          const taskMarker = getMarkerStyle(task.due_date, markerColor);
                          const statusStyle =
                            task.status === 'Complete' ? { backgroundColor: '#DCFCE7', color: '#15803D' } :
                            task.status === 'Requested' ? { backgroundColor: '#FEF3C7', color: '#A16207' } :
                            task.status === 'N/A' ? { backgroundColor: '#F1F5F9', color: '#64748B' } :
                            { backgroundColor: '#EFF4FB', color: '#1A3665' };

                          return (
                            <div
                              key={task.id}
                              className="flex transition-colors"
                              style={{ backgroundColor: '#FAFBFD' }}
                            >
                              <div
                                className="w-72 flex-shrink-0 px-4 py-1.5 flex items-center gap-2 pl-12"
                                style={{ borderRight: '1px solid #F0F2F6' }}
                              >
                                {task.is_doc_request ? (
                                  <FileText className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#94A3B8' }} />
                                ) : (
                                  <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#94A3B8' }} />
                                )}
                                <span
                                  className="text-[12px] truncate"
                                  style={{ color: '#334155' }}
                                  title={task.name}
                                >
                                  {task.name}
                                </span>
                                <span className="text-[10px] tabular-nums flex-shrink-0" style={{ color: '#94A3B8' }}>
                                  {new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                                <span
                                  className="text-[10px] ml-auto flex-shrink-0 px-1.5 py-0.5 rounded-md font-semibold uppercase"
                                  style={{ ...statusStyle, letterSpacing: '0.04em' }}
                                >
                                  {task.status}
                                </span>
                              </div>
                              <div className="flex-1 relative py-1.5">
                                {todayPosition !== null && (
                                  <div
                                    className="absolute top-0 bottom-0 z-10"
                                    style={{ left: `${todayPosition}%`, width: '2px', backgroundColor: '#EF4444' }}
                                  />
                                )}
                                {taskMarker && (
                                  <div
                                    className="absolute w-3 h-3 rounded-full top-1/2 -translate-y-1/2 -translate-x-1/2"
                                    style={{
                                      ...taskMarker,
                                      border: '2px solid white',
                                      boxShadow: `0 1px 3px ${markerColor}55`,
                                    }}
                                    title={`${task.name}: Due ${new Date(task.due_date).toLocaleDateString()}`}
                                  />
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Legend */}
      <div
        className="px-4 py-2.5 flex items-center gap-6 flex-wrap"
        style={{ backgroundColor: '#FAFBFD', borderTop: '1px solid #E5E9F0' }}
      >
        <span
          className="text-[10px] font-semibold uppercase"
          style={{ letterSpacing: '0.08em', color: '#64748B' }}
        >
          Legend
        </span>
        <div className="flex items-center gap-4 flex-wrap">
          {PHASES.slice(0, 5).map(phase => (
            <div key={phase.id} className="flex items-center gap-1.5 text-[11px]" style={{ color: '#475569' }}>
              <span className="w-3 h-2.5 rounded-sm" style={{ backgroundColor: phase.color }} />
              <span>{phase.name}</span>
            </div>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1.5 text-[11px]" style={{ color: '#475569' }}>
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: LFG_COLORS.green }} />
            <span>Complete</span>
          </div>
          <div className="flex items-center gap-1.5 text-[11px]" style={{ color: '#475569' }}>
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#EAB308' }} />
            <span>Requested</span>
          </div>
          <div className="flex items-center gap-1.5 text-[11px]" style={{ color: '#475569' }}>
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#1A3665' }} />
            <span>Pending</span>
          </div>
          <div className="flex items-center gap-1.5 text-[11px]" style={{ color: '#475569' }}>
            <span className="w-0.5 h-3.5" style={{ backgroundColor: '#EF4444' }} />
            <span>Today</span>
          </div>
        </div>
      </div>
      </div>{/* end timeline card */}
    </div>
  );
}
